import { compose, createSelector } from '@reduxjs/toolkit';
import { makeSelectAuthApiConfig } from 'services/api/api.selectors';
import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';
import { AppState, ReduxSelector } from 'store/types';
import { Channel } from 'utils/config.config';
import { logoutUserEndpoint, verifySessionEndpoint } from './session.config';
import { Auth, SessionInfo } from './session.types';

export const selectAuth = (reduxState: AppState) => reduxState.session.auth;
export const selectAuthRequestId = (reduxState: AppState) =>
  reduxState.session.auth.requestId;

export const selectSessionInfo: ReduxSelector<Partial<SessionInfo>> = (
  reduxState: AppState,
) => {
  return reduxState.session.info;
};

export const selectChannel: ReduxSelector<Channel | undefined> = (
  reduxState: AppState,
) => {
  return reduxState.session.info.channel;
};

export const selectApiAuthHeaders: ReduxSelector<{
  authorization?: string | null;
}> = createSelector(selectAuth, (auth) => {
  if (auth.authToken) {
    return {
      authorization: `Bearer\u0020${auth.authToken}`,
    };
  }

  // This token has a valid syntax but is expired:
  // We use this token to trigger consistent '401' responses.
  return {
    authorization: 'Bearer\u0020',
  };
});

export const selectAuthToken: ReduxSelector<string | null> = compose(
  (auth: Auth) => auth.authToken,
  selectAuth,
);
export const selectVerifiedDate: ReduxSelector<string | null> = compose(
  (auth: Auth) => auth.verifiedAt,
  selectAuth,
);

/**
 * Returns 'true' if the session has been validated.
 * A session is valid until the api deems it so.
 * Every successful api request with auth refreshes the token.
 */
export const selectIsSessionValidated: ReduxSelector<boolean> = compose(
  (auth: Partial<Auth>) =>
    typeof auth.authToken === 'string' && typeof auth.verifiedAt === 'string',
  selectAuth,
);

/**
 * Extracts the object we pass to 'requestDataIfNecessary'.
 * @param reduxState
 * "see AppState
 */
export const selectVerifySessionEndpointApiConfig: ReduxSelector<
  RequestApiDataIfNecessaryPayload
> = (reduxState: AppState) => {
  return {
    resourceId: verifySessionEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      url: verifySessionEndpoint,
      method: 'GET',
      enrichRequestData: false,
    })(reduxState),
  };
};

export const selectUrlBasename = createSelector(
  selectSessionInfo,
  (sessionInfo) => sessionInfo?.homepage?.pathname ?? '',
);

export function selectLogoutUserApiConfig(
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload {
  return {
    resourceId: logoutUserEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      method: 'POST',
      url: logoutUserEndpoint,
      enrichRequestData: false,
    })(reduxState),
    invalidateIfPresent: true,
    skipDefaultServerErrorHandling: true,
  };
}
